// Core packages

// Third party packages

// Custom packages
import { DocumentArchiveDocListItem } from "src/app/shared/interfaces/document-archive-doc.interface";
import { DocumentArchiveSearchLabel } from "src/app/shared/interfaces/document-archive-search.interface";

/**
 * Pure rules of the dialog that lists the documents of ONE mapping element on the 3.0.0 archive,
 * before «Rifare impianto» and before «Elimina impianto» / «Elimina la scheda» (MAP-A1).
 *
 * At the switch that dialog takes the place of the two legacy ones (`mapping-remake-dialog`,
 * `mapping-delete-dialog`), which read the documents of the element out of the legacy archive
 * through `getDocsFromArchive` of the mapping tab. Its list is the legacy one ON PURPOSE, defect
 * included (decision of 26 Oct 2026): a document type disappears whole when one of its rows has no
 * `data`.
 *
 * The rows are the ones `GET /document-archive-docs?documentArchiveId&structureObjectId` serves, each
 * naming its type (`documentType`, back-end 0e242629): nothing here calls the back-end.
 *
 * Loaded with the mapping tab, i.e. in the initial bundle: never import from here the lazy-only
 * helpers of the archive pages (`document-archive-insert.utils`, `document-archive-row-actions.utils`).
 *
 * @since 3.0.0
 */

/** Rows asked for in one read: the cap of the back-end. An element holds a few dozen (35 on CARBONERA 1). */
export const MAPPING_DOCS_PAGE_SIZE = 500;

/**
 * Pages read at most for one element (10.000 rows): a guard against a total that keeps growing while
 * it is read, not a size a real element reaches.
 */
export const MAPPING_DOCS_MAX_PAGES = 20;

/**
 * Rows the back-end carries over in one remake. With more, it copies NOTHING and only logs why, while
 * the new element is created all the same: the dialog never sends more.
 */
export const MAX_REMAKE_PLANT_DOCS = 200;

/**
 * One row of the dialog: one document of the element, with the columns of the legacy dialog.
 *
 * @since 3.0.0
 */
export interface MappingArchiveDocRow {
  /** `_id` of the archive row: what «Rifare impianto» hands back */
  docId: string;
  /** Name of the document type in the language of the session, "" when the type is gone */
  title: string;
  /** `data.contenutoDocumento`, the "Descrizione" column */
  content: string;
  /** `data.dataDocumento`, the "Data" column; `null` when it is not a date the date pipe can read */
  docDate: string | number | null;
  /** `data.document`: the files offered by «Scarica» */
  files: unknown[];
  /** `data.barCode`: names the zip of «Scarica tutti» when the response does not */
  barCode: string;
  /** The tick of «Rifare impianto» */
  selected: boolean;
}

/**
 * What «Rifare impianto» adds to the POST that creates the new element, as `remakePlantDocs`
 * (`POST /api/v2/condo-structures/:condoStructureId/:structureType`, MAP-A1).
 *
 * @since 3.0.0
 */
export interface RemakePlantSelection {
  /** The element being remade: where the copied rows come from */
  sourceStructureId: string;
  /** The ticked rows, by their own id, each once, at most @see MAX_REMAKE_PLANT_DOCS */
  documentArchiveDocIds: string[];
}

/**
 * What to do after one page of the element has arrived.
 *
 * @since 3.0.0
 */
export type MappingDocsNextRead =
  /** Read on, from this offset */
  | { kind: "next"; skip: number }
  /** Every row has arrived */
  | { kind: "done" }
  /** Rows are left but the reads are over: the list is NOT complete and must not be offered */
  | { kind: "tooMany" };

/**
 * The name of a document type in the language of the session.
 *
 * Italian on "it", English on anything else, as the legacy dialog reads its own map. A type the
 * back-end could not resolve comes with `translation: null` and gets an empty name — the legacy
 * dialog left the title empty for a key missing from its map — never the internal `title`.
 *
 * @since 3.0.0
 *
 * @param type The `documentType` of the row
 * @param lang Language of the session (cookie `lang`)
 */
export function mappingDocTypeTitle(
  type: DocumentArchiveSearchLabel | null | undefined,
  lang: string
): string {
  if (!type || !type.translation) {
    return "";
  }
  const name = lang === "it" ? type.translation.it : type.translation.en;
  return typeof name === "string" ? name : "";
}

/** What the "Descrizione" column prints: a text or a number as they are, nothing else */
function textOf(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  return typeof value === "number" && isFinite(value) ? String(value) : "";
}

/**
 * A value the date pipe can print, or `null`. The legacy dialog hands the raw value to the pipe, which
 * throws on what it cannot read and breaks the whole dialog: a stored value that is not a date leaves
 * its cell empty here instead.
 */
function dateOf(value: unknown): string | number | null {
  if (typeof value === "number") {
    return isFinite(value) ? value : null;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return null;
  }
  // What the pipe reads: a number written as a string, or anything Date understands
  return !isNaN(Number(value)) || !isNaN(new Date(value).getTime()) ? value : null;
}

/** One row of the dialog; `item.data` is known to be there */
function toMappingArchiveDocRow(item: DocumentArchiveDocListItem, lang: string): MappingArchiveDocRow {
  const data = item.data;
  return {
    docId: typeof item._id === "string" ? item._id : "",
    title: mappingDocTypeTitle(item.documentType, lang),
    content: textOf(data.contenutoDocumento),
    docDate: dateOf(data.dataDocumento),
    files: Array.isArray(data.document) ? data.document : [],
    barCode: typeof data.barCode === "string" ? data.barCode : "",
    selected: false,
  };
}

/** Non-empty strings, each once, first occurrence kept */
function uniqueIds(ids: ReadonlyArray<string>): string[] {
  const unique: string[] = [];
  ids.forEach((id) => {
    if (typeof id === "string" && id !== "" && unique.indexOf(id) === -1) {
      unique.push(id);
    }
  });
  return unique;
}

/**
 * The rows of the dialog out of the rows of one element, by the rule of the legacy dialog.
 *
 * The rows are grouped by document type in the order they arrive: the back-end sorts them by type,
 * then by creation date, so the copies of one type already sit together. A type with a row deleted or
 * without `data` disappears WHOLE — `getDocsFromArchive` of the mapping tab deletes the key of the
 * type — and every row of the other types is shown. It hides documents actually filed next to an
 * empty row of the same type, such as a renewal still to be filed: kept on purpose (26 Oct 2026),
 * the new dialog offers what the old one offers. `data: {}` is NOT missing data, as in the legacy.
 *
 * @since 3.0.0
 *
 * @param items The rows of the element, every page of them
 * @param lang  Language of the session (cookie `lang`)
 */
export function mappingArchiveDocRows(
  items: ReadonlyArray<DocumentArchiveDocListItem> | null | undefined,
  lang: string
): MappingArchiveDocRow[] {
  if (!Array.isArray(items)) {
    return [];
  }

  const typeOrder: string[] = [];
  const rowsByType: { [typeId: string]: DocumentArchiveDocListItem[] } = {};
  items.forEach((item) => {
    if (!item) {
      return;
    }
    const typeId = typeof item.documentTemplateDocId === "string" ? item.documentTemplateDocId : "";
    if (!Object.prototype.hasOwnProperty.call(rowsByType, typeId)) {
      rowsByType[typeId] = [];
      typeOrder.push(typeId);
    }
    rowsByType[typeId].push(item);
  });

  const rows: MappingArchiveDocRow[] = [];
  typeOrder.forEach((typeId) => {
    const group = rowsByType[typeId];
    if (group.some((item) => item.deleted === true || !item.data)) {
      return;
    }
    group.forEach((item) => rows.push(toMappingArchiveDocRow(item, lang)));
  });
  return rows;
}

/**
 * Where the next read of the element starts, after a page has arrived.
 *
 * Done when every row the total announces has arrived, or when a page comes back empty (the total
 * moved while reading). After @see MAPPING_DOCS_MAX_PAGES pages with rows still announced, the list
 * is reported as incomplete rather than cut silently: a cut list would hide documents from the remake.
 *
 * @since 3.0.0
 *
 * @param readSoFar  Rows arrived so far, this page included
 * @param pageLength Rows of the page just arrived
 * @param totalCount `totalCount` of that page
 * @param pagesRead  Pages read so far, this one included
 */
export function nextMappingDocsRead(
  readSoFar: number,
  pageLength: number,
  totalCount: number,
  pagesRead: number
): MappingDocsNextRead {
  if (pageLength <= 0 || readSoFar >= totalCount) {
    return { kind: "done" };
  }
  if (pagesRead >= MAPPING_DOCS_MAX_PAGES) {
    return { kind: "tooMany" };
  }
  return { kind: "next", skip: readSoFar };
}

/**
 * The ids of the ticked rows, each once, in the order of the list.
 *
 * @since 3.0.0
 *
 * @param rows The rows of the dialog
 */
export function selectedMappingDocIds(rows: ReadonlyArray<MappingArchiveDocRow> | null | undefined): string[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return uniqueIds(rows.filter((row) => row && row.selected).map((row) => row.docId));
}

/**
 * True when the ticks are more than the back-end carries over in one remake.
 *
 * @since 3.0.0
 *
 * @param count Rows ticked
 */
export function exceedsRemakePlantLimit(count: number): boolean {
  return count > MAX_REMAKE_PLANT_DOCS;
}

/**
 * The `remakePlantDocs` of the POST that creates the new element: the element being remade and the
 * ticked rows by id, each once, blanks dropped. An empty list is a remake that copies nothing, as
 * today with nothing ticked.
 *
 * @since 3.0.0
 *
 * @param sourceStructureId     `structureId` of the element being remade
 * @param documentArchiveDocIds Ids of the ticked rows
 */
export function remakePlantSelection(
  sourceStructureId: string,
  documentArchiveDocIds: ReadonlyArray<string> | null | undefined
): RemakePlantSelection {
  return {
    sourceStructureId,
    documentArchiveDocIds: Array.isArray(documentArchiveDocIds) ? uniqueIds(documentArchiveDocIds) : [],
  };
}
